import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiTarget, FiSettings, FiBookOpen, FiBarChart3, FiCheckCircle } = FiIcons;

const Solutions = () => {
  const solutions = [
    {
      icon: FiTarget,
      title: 'Risk Assessment & Management',
      description: 'Comprehensive risk profiling tailored to your business model, industry, and geographic exposure.',
      features: ['Customer risk categorization', 'Transaction monitoring setup', 'Risk appetite definition', 'Ongoing risk reviews']
    },
    {
      icon: FiSettings,
      title: 'Policy & Procedure Development',
      description: 'Custom AML/CFT frameworks designed specifically for your organization\'s operational structure.',
      features: ['Tailored policy creation', 'Process documentation', 'Control implementation', 'Staff training materials']
    },
    {
      icon: FiBookOpen,
      title: 'Regulatory Reporting',
      description: 'Expert preparation and submission of all required regulatory reports and suspicious activity reports.',
      features: ['SAR/STR preparation', 'Regulatory submissions', 'Authority liaison', 'Compliance documentation']
    },
    {
      icon: FiBarChart3,
      title: 'Ongoing Monitoring',
      description: 'Continuous oversight ensuring your compliance framework remains effective and current.',
      features: ['Monthly compliance reviews', 'Regulatory update alerts', 'Performance metrics', 'Improvement recommendations']
    }
  ];

  const processSteps = [
    { number: '01', title: 'Discovery', desc: 'Deep dive into your business model and risk profile' },
    { number: '02', title: 'Framework Design', desc: 'Custom compliance architecture creation' },
    { number: '03', title: 'Implementation', desc: 'Seamless deployment with team training' },
    { number: '04', title: 'Continuous Support', desc: 'Ongoing monitoring and optimization' }
  ];

  return (
    <section id="solutions" className="py-24 bg-gradient-to-br from-emerald-50 to-green-50 relative overflow-hidden">
      <div className="absolute top-1/4 left-0 w-96 h-96 bg-emerald-200 rounded-full blur-3xl opacity-30"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-emerald-600 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
            <SafeIcon icon={FiShield} className="w-4 h-4" />
            <span>Comprehensive Solutions</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Expert vMLRO Services
            <br />
            <span className="text-emerald-700">Tailored to Your Needs</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Our virtual Money Laundering Reporting Officer services provide the expertise you need 
            without the overhead of full-time staff. Every solution is customized for perfect fit.
          </p>
        </motion.div>

        <div className="space-y-8 mb-20">
          {solutions.map((solution, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-emerald-500"
            >
              <div className="grid lg:grid-cols-3 gap-8 items-start">
                <div className="lg:col-span-1">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-600 to-green-700 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                    <SafeIcon icon={solution.icon} className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {solution.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{solution.description}</p>
                </div>

                <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
                  {solution.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-start space-x-3 bg-emerald-50 rounded-xl p-4">
                      <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="bg-gradient-to-r from-emerald-700 to-green-800 rounded-3xl p-10 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSIjZmZmIiBzdHJva2Utd2lkdGg9IjIiIG9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-20"></div>
          
          <div className="relative">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Our Implementation Process
            </h3>
            <div className="grid md:grid-cols-4 gap-6">
              {processSteps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 h-full border border-white/20">
                    <div className="text-5xl font-bold text-emerald-300 mb-4 opacity-50" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      {step.number}
                    </div>
                    <h4 className="text-white font-bold text-lg mb-2">{step.title}</h4>
                    <p className="text-emerald-100 text-sm">{step.desc}</p>
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-white/30"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Solutions;